/**
 * CreateStreamPage - 简化版直播间创作页面（手机端竖屏风格）
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { streamAPI } from '../services/api';
import { getUserSessionId } from '../utils/userSession';
import userAuthService from '../services/userAuthService';
import SidebarNav from './common/SidebarNav';

// 创建过程中的提示文案
const TIPS = [
  "AI正在学习你的角色特征...",
  "生成多个互动动作，让互动更精彩",
  "首次创建需要1-2分钟，请耐心等待",
  "完成后可以立即开始互动体验",
  "每个角色都是独一无二的"
];

// 后续生成中的提示
const CONTINUE_TIPS = [
  "第一个视频已完成，可以开始互动了",
  "后台正在生成更多互动动作",
  "新动作生成后会自动添加到互动空间",
];

// 创建步骤
const STEPS = [
  { id: 'upload', label: '上传', icon: '📤' },
  { id: 'analyze', label: '分析', icon: '🔍' },
  { id: 'generate', label: '生成', icon: '✨' },
  { id: 'render', label: '渲染', icon: '🎬' },
];

// 本地存储key
const STORAGE_KEY = 'creating_stream_state';

const CreateStreamPage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    character: null
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState('');
  const [progressPercent, setProgressPercent] = useState(0);
  const [userSessionId, setUserSessionId] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [currentTip, setCurrentTip] = useState(0);
  const [characterPreview, setCharacterPreview] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [startTime, setStartTime] = useState(null);
  const [firstVideoReady, setFirstVideoReady] = useState(false); // 第一个视频是否已完成
  const [creatingStreamId, setCreatingStreamId] = useState(null); // 正在创建的直播间ID
  const [isManualEntry, setIsManualEntry] = useState(false); // 是否是用户主动进入（非首次创建）
  
  const pollingRef = useRef(null);
  const tipIntervalRef = useRef(null);

  // 初始化
  useEffect(() => {
    const sessionId = getUserSessionId();
    setUserSessionId(sessionId);
    // 从 token 解析当前用户 id
    try {
      const token = userAuthService.getAccessToken();
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setCurrentUserId(Number(payload.sub));
      }
    } catch (_) { /* ignore */ }
  }, []);

  // 检查是否有正在创建的任务
  useEffect(() => {
    if (!userSessionId) return;
    
    const checkExistingCreation = async () => {
      try {
        // 先检查本地存储
        const savedState = localStorage.getItem(STORAGE_KEY);
        let savedPreview = null;
        let savedHasJumped = false;
        if (savedState) {
          const parsed = JSON.parse(savedState);
          savedPreview = parsed.characterPreview;
          savedHasJumped = parsed.hasAutoJumped || false;
          if (parsed.startTime) {
            setStartTime(new Date(parsed.startTime));
          }
        }
        
        // 检查后端状态
        const response = await streamAPI.getUserCreationStatus(currentUserId, userSessionId);
        
        if (response.data.has_creating_stream) {
          const stream = response.data.stream;
          const progress = stream.creation_progress || 0;
          const status = stream.creation_status;
          
          setCreatingStreamId(stream.id);
          
          // 判断第一个视频是否已完成
          const isFirstVideoReady = ['active', 'completed', 'pending_selection', 'completed_with_errors'].includes(status);
          
          if (isFirstVideoReady) {
            setFirstVideoReady(true);
            
            // 如果已经完成，清除存储
            if (status === 'completed' || status === 'completed_with_errors') {
              localStorage.removeItem(STORAGE_KEY);
            }
          }
          
          // 用户主动进入创作页面，标记为手动进入，不自动跳转
          setIsManualEntry(true);
          
          // 显示创建/继续生成状态
          setCreating(true);
          setProgressPercent(progress);
          updateStepByProgress(progress);
          
          if (savedPreview) {
            setCharacterPreview(savedPreview);
          }
          
          // 如果还没完成，继续轮询
          if (status !== 'completed' && status !== 'completed_with_errors') {
            startPolling(stream.id);
          }
    } else {
          // 没有正在创建的任务，清除本地存储
          localStorage.removeItem(STORAGE_KEY);
        }
    } catch {
        // ignore
    } finally {
        setCheckingStatus(false);
      }
    };
    
    checkExistingCreation();
  }, [userSessionId, currentUserId, navigate]);

  // 根据进度更新当前步骤
  const updateStepByProgress = (progress) => {
    if (progress < 20) {
      setCurrentStep(0);
      setGenerationProgress('上传角色图片...');
    } else if (progress < 40) {
      setCurrentStep(1);
      setGenerationProgress('分析角色特征...');
    } else if (progress < 70) {
      setCurrentStep(2);
      setGenerationProgress('生成互动动作...');
        } else {
      setCurrentStep(3);
      setGenerationProgress('渲染视频中...');
    }
  };

  // 循环显示提示文案
  useEffect(() => {
    if (creating) {
      const tips = firstVideoReady ? CONTINUE_TIPS : TIPS;
      tipIntervalRef.current = setInterval(() => {
        setCurrentTip(prev => (prev + 1) % tips.length);
      }, 4000);
    }
    return () => {
      if (tipIntervalRef.current) {
        clearInterval(tipIntervalRef.current);
      }
    };
  }, [creating, firstVideoReady]);

  const isFormValid = formData.title.trim().length >= 2 && formData.character;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('请选择图片文件');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError('图片大小不能超过10MB');
        return;
      }
      setFormData(prev => ({ ...prev, character: file }));
      const previewUrl = URL.createObjectURL(file);
      setCharacterPreview(previewUrl);
      setError(null);
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({ ...prev, character: null }));
    setCharacterPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // 普通轮询（用户主动进入查看进度，不自动跳转）
  const startPolling = useCallback((streamId) => {
    if (pollingRef.current) return;
    
    pollingRef.current = setInterval(async () => {
      try {
        const response = await streamAPI.getUserCreationStatus(currentUserId, userSessionId);
        
        if (response.data.has_creating_stream) {
          const stream = response.data.stream;
          const progress = stream.creation_progress || 0;
          const status = stream.creation_status;
          
          setProgressPercent(progress);
          updateStepByProgress(progress);
          
          // 第一个视频完成
          if (['active', 'completed', 'pending_selection', 'completed_with_errors'].includes(status)) {
            setFirstVideoReady(true);
            
            // 全部完成
            if (status === 'completed' || status === 'completed_with_errors') {
              stopPolling();
              localStorage.removeItem(STORAGE_KEY);
              setGenerationProgress('全部完成！');
            }
          } else if (status === 'failed') {
            stopPolling();
            localStorage.removeItem(STORAGE_KEY);
            setCreating(false);
            setError(stream.creation_message || '创建失败，请重试');
          }
        } else {
          // 没有正在创建的任务
          stopPolling();
          localStorage.removeItem(STORAGE_KEY);
          setCreating(false);
        }
      } catch { /* ignore */ }
    }, 3000);
  }, [currentUserId, userSessionId]);

  // 带自动跳转的轮询（首次创建时使用）
  const startPollingWithAutoJump = useCallback((streamId) => {
    if (pollingRef.current) return;
    
    pollingRef.current = setInterval(async () => {
      try {
        const response = await streamAPI.getUserCreationStatus(currentUserId, userSessionId);
        
        if (response.data.has_creating_stream) {
          const stream = response.data.stream;
          const progress = stream.creation_progress || 0;
          const status = stream.creation_status;
          
          setProgressPercent(progress);
          updateStepByProgress(progress);
          
          // 第一个视频完成，自动跳转到互动空间
          if (['active', 'completed', 'pending_selection', 'completed_with_errors'].includes(status)) {
            stopPolling();
            localStorage.removeItem(STORAGE_KEY);
            setCreating(false);
            navigate(`/stream/${streamId}`);
            return;
          } else if (status === 'failed') {
            stopPolling();
            localStorage.removeItem(STORAGE_KEY);
            setCreating(false);
            setError(stream.creation_message || '创建失败，请重试');
          }
        } else {
          stopPolling();
          localStorage.removeItem(STORAGE_KEY);
          setCreating(false);
        }
      } catch { /* ignore */ }
    }, 3000);
  }, [currentUserId, userSessionId, navigate]);

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  useEffect(() => () => {
    stopPolling();
    if (tipIntervalRef.current) {
      clearInterval(tipIntervalRef.current);
    }
  }, []);

  const handleSubmit = async () => {
    if (!isFormValid || loading) return;
    
    setError(null);
    setLoading(true);
    
    try {
      const submitData = new FormData();
      submitData.append('title', formData.title.trim());
      submitData.append('description', formData.description.trim());
      submitData.append('character', formData.character);
      if (userSessionId) submitData.append('user_session_id', userSessionId);
      
      const response = await streamAPI.createStream(submitData);
      const now = new Date();
      
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        streamId: response.data.id,
        startTime: now.toISOString(),
        characterPreview: characterPreview,
        hasAutoJumped: false
      }));
      
      const newStreamId = response.data.id;
      setCreatingStreamId(newStreamId);
      setStartTime(now);
      setCreating(true);
      setLoading(false);
      setCurrentStep(0);
      setProgressPercent(5);
      setGenerationProgress('上传角色图片...');
      setFirstVideoReady(false);
      setIsManualEntry(false); // 这是新创建，不是手动进入
      
      // 启动轮询，并在首次完成时跳转
      startPollingWithAutoJump(newStreamId);
      
    } catch (err) {
      setLoading(false);
      setError(err.response?.status === 409 
        ? '您有一个正在创建的互动空间' 
        : (err.response?.data?.message || '创建失败'));
    }
  };

  const handleCancel = () => {
    if (creating) {
      stopPolling();
    }
    navigate('/');
  };

  // 进入互动空间
  const handleEnterStream = () => {
    stopPolling();
    localStorage.removeItem(STORAGE_KEY);
    if (creatingStreamId) {
      navigate(`/stream/${creatingStreamId}`);
        } else {
      navigate('/');
    }
  };

  // 计算已用时间
  const getElapsedTime = () => {
    if (!startTime) return '';
    const elapsed = Math.floor((Date.now() - startTime.getTime()) / 1000);
    const mins = Math.floor(elapsed / 60);
    const secs = elapsed % 60;
    return mins > 0 ? `${mins}分${secs}秒` : `${secs}秒`;
  };

  // 强制重置
  const handleForceReset = async () => {
    stopPolling();
    
    // 如果有正在创建的互动空间，调用后端API取消
    if (creatingStreamId) {
      try {
        await streamAPI.cancelStreamCreation(creatingStreamId);
      } catch { /* proceed with reset even on failure */ }
    }
    
    localStorage.removeItem(STORAGE_KEY);
    setCreating(false);
    setFirstVideoReady(false);
    setError(null);
    setProgressPercent(0);
    setCreatingStreamId(null);
    setCharacterPreview(null);
    setFormData({ title: '', description: '', character: null });
  };

  // 检查状态中
  if (checkingStatus) {
  return (
      <div className="create-stream-page">
        <SidebarNav />
        <div className="create-stream-frame">
          <div className="checking-state">
            <div className="checking-spinner"></div>
            <p>检查创建状态...</p>
            <button className="force-reset-link" onClick={handleForceReset}>
              卡住了？点击重置
            </button>
          </div>
          </div>
          </div>
    );
  }

  // 生成中状态
  if (creating) {
    const tips = firstVideoReady ? CONTINUE_TIPS : TIPS;
    
    return (
      <div className="create-stream-page">
        <SidebarNav />
        <div className="create-stream-frame">
          <div className="generating-state">
            {/* 角色图片预览 */}
            {characterPreview && (
              <div className="char-preview-box">
                <img src={characterPreview} alt="" className="char-preview-img" />
                {!firstVideoReady && <div className="char-preview-ring"></div>}
                      </div>
                    )}
            
            {/* 进度圆环 */}
            <div className="progress-circle">
              <svg viewBox="0 0 100 100">
                <circle className="progress-bg" cx="50" cy="50" r="42" />
                <circle 
                  className="progress-bar" 
                  cx="50" cy="50" r="42"
                  strokeDasharray={264}
                  strokeDashoffset={264 - (264 * progressPercent / 100)}
                />
              </svg>
              <span className="progress-num">{progressPercent}%</span>
                    </div>
                    
            {/* 步骤进度 */}
            <div className="steps-row">
              {STEPS.map((step, idx) => (
                <div key={step.id} className={`step-item ${idx < currentStep ? 'done' : idx === currentStep ? 'active' : ''}`}>
                  <span className="step-icon">{idx < currentStep ? '✓' : step.icon}</span>
                  <span className="step-label">{step.label}</span>
                      </div>
              ))}
                      </div>
            
            {/* 状态文字 */}
            {firstVideoReady ? (
              <p className="progress-status ready">✓ 互动空间已就绪</p>
            ) : (
              <p className="progress-status">{generationProgress}</p>
            )}
            
            {/* 提示文案 */}
            <p className="progress-tip">{tips[currentTip % tips.length]}</p>
            
            {/* 已用时间 */}
            {startTime && !firstVideoReady && (
              <p className="elapsed-time">已用时 {getElapsedTime()}</p>
            )}
            
            {/* 操作按钮 */}
            {firstVideoReady ? (
              <button className="enter-btn" onClick={handleEnterStream}>
                进入互动空间
                            </button>
            ) : (
              <div className="action-links">
                <button className="cancel-link" onClick={handleCancel}>暂时离开</button>
                <button className="force-reset-link" onClick={handleForceReset}>卡住了？重置</button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
    );
  }

  // 表单
  return (
    <div className="create-stream-page">
      <SidebarNav />
      <div className="create-stream-frame">
        {/* 顶栏 */}
        <div className="create-topbar">
          <button className="back-btn" onClick={() => navigate('/')}>←</button>
          <span className="topbar-title">创建互动空间</span>
          <div style={{width: 32}}></div>
            </div>

        {/* 表单内容 */}
        <div className="create-form-area">
          {error && (
            <div className="error-box">
              <div className="error-tip">⚠️ {error}</div>
              <button className="retry-btn" onClick={() => setError(null)}>重新创建</button>
            </div>
          )}

          {/* 角色上传 */}
          <div className="form-item">
            <div className="item-label">角色图片 <span className="req">*</span></div>
            <div 
              className={`upload-box ${formData.character ? 'has-img' : ''}`}
              onClick={() => !formData.character && fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                hidden
                />
                {formData.character ? (
                <div className="preview-wrap">
                  <img src={characterPreview} alt="" className="preview-img" />
                  <button className="remove-btn" onClick={(e) => { e.stopPropagation(); handleRemoveImage(); }}>✕</button>
                  </div>
                ) : (
                <div className="upload-hint">
                  <span className="hint-icon">📷</span>
                  <span>点击上传</span>
                  </div>
                )}
              </div>
            </div>

          {/* 标题 */}
          <div className="form-item">
            <div className="item-label">标题 <span className="req">*</span></div>
                <input
              type="text"
              name="title"
              className="text-input"
              value={formData.title} 
              onChange={handleInputChange}
              placeholder="给互动空间起个名字"
              maxLength={50}
            />
          </div>

          {/* 描述 */}
          <div className="form-item">
            <div className="item-label">描述</div>
            <textarea 
              name="description"
              className="text-area"
              value={formData.description} 
              onChange={handleInputChange}
              placeholder="介绍一下（可选）"
              rows={2}
              maxLength={200}
            />
              </div>
            </div>

        {/* 底部按钮 */}
        <div className="create-bottom">
              <button
            className={`submit-btn ${!isFormValid || loading ? 'disabled' : ''}`}
            disabled={!isFormValid || loading}
            onClick={handleSubmit}
          >
            {loading ? '提交中...' : '开始创作'}
              </button>
            </div>
      </div>
    </div>
  );
};

export default CreateStreamPage;
